import {
    nextTick,
    ref,
    watch,
    type ComponentInternalInstance,
    type Ref,
} from "vue";
import { type ListController } from "../../list-controller";
import {
    type AdvertItem,
    type FillerItem,
    type ProductGalleryItem,
    type ProductListItem,
} from "../../list-item";

type Item =
    | ProductListItem
    | AdvertItem
    | ProductGalleryItem
    | FillerItem
    | { type: never; filler: true };

interface Observator {
    instance: ComponentInternalInstance | null;
    getItem: () => Item;
    getIsLast: () => boolean;
    getIsFirstOfPage: () => boolean;
    getListController: () => ListController | undefined;
    hasToldToObserve: Ref<boolean>;
    hasToldToObserveFirstOfPage: Ref<boolean>;
}

export function createItemObservator(
    instance: ComponentInternalInstance | null,
    getIsLast: () => boolean,
    getItem: () => Item,
    getListController: () => ListController | undefined,
    getIsFirstOfPage: () => boolean
) {
    if (instance == null) throw new Error("missing instance");
    if (isFiller(getItem())) return undefined;
    const hasToldToObserve = ref(false);
    const hasToldToObserveFirstOfPage = ref(false);

    const observator: Observator = {
        instance,
        getItem,
        getIsLast,
        getIsFirstOfPage,
        getListController,
        hasToldToObserve,
        hasToldToObserveFirstOfPage,
    };

    return {
        start(props: { isLast: boolean; isFirstOfPage: boolean }) {
            updateLazyLoadObservation(observator);
            updateLoadMorePageObservation(observator);
            watch(
                () => props.isLast,
                () => {
                    nextTick(function () {
                        updateLazyLoadObservation(observator);
                    });
                }
            );

            watch(
                () => props.isFirstOfPage,
                () => {
                    nextTick(function () {
                        updateLoadMorePageObservation(observator);
                    });
                }
            );
        },
        stop() {
            unobserveIfNotDone(observator);
            unobserveFirstOfPageIfNotDone(observator);
        },
        updateLazyLoadObservation() {
            updateLazyLoadObservation(observator);
        },
        updateLoadMorePageObservation() {
            updateLoadMorePageObservation(observator);
        },
    };
}
function updateLazyLoadObservation(observator: Observator) {
    //console.log("updateLazyLoadObservation");
    if (observator.getIsLast() === true) {
        observeIfNotDone(observator);
    } else {
        unobserveIfNotDone(observator);
    }
}

function observeIfNotDone(observator: Observator) {
    if (observator.instance == null) throw new Error("missing instance");
    if (isFiller(observator.getItem())) return;
    if (observator.hasToldToObserve.value !== true) {
        //console.debug("reportAsLastitem");
        observator
            .getListController()
            ?.reportAsLastItem(observator.instance.proxy?.$el);
        observator.hasToldToObserve.value = true;
    }
}
function isFiller(item: Item) {
    return item.type == "filler" || ("filler" in item && item.filler);
}

function unobserveIfNotDone(observator: Observator) {
    if (observator.instance == null) throw new Error("missing instance");
    if (isFiller(observator.getItem())) return;
    if (observator.hasToldToObserve.value === true) {
        // console.debug("unreportAsLastItem");
        observator
            .getListController()
            ?.unreportAsLastItem(observator.instance.proxy?.$el);
        observator.hasToldToObserve.value = false;
    }
}

function updateLoadMorePageObservation(observator: Observator) {
    if (observator.getIsFirstOfPage() === true) {
        observeFirstOfPageIfNotDone(observator);
    } else {
        unobserveFirstOfPageIfNotDone(observator);
    }
}
function observeFirstOfPageIfNotDone(observator: Observator) {
    if (observator.instance == null) throw new Error("missing instance");
    const item = observator.getItem();
    if (item.type == "filler" || ("filler" in item && item.filler)) return;
    if (observator.hasToldToObserveFirstOfPage.value !== true) {
        // console.debug("reportAsFirstOfPage");
        observator
            .getListController()
            ?.reportAsFirstOfPage(observator.instance.proxy?.$el);
        observator.hasToldToObserveFirstOfPage.value = true;
    }
}
function unobserveFirstOfPageIfNotDone(observator: Observator) {
    if (observator.instance == null) throw new Error("missing instance");
    const item = observator.getItem();
    if (item.type == "filler" || ("filler" in item && item.filler)) return;
    if (observator.hasToldToObserveFirstOfPage.value === true) {
        // console.debug("unreportAsFirstOfPage");
        observator
            .getListController()
            ?.unreportAsFirstOfPage(observator.instance.proxy?.$el);
        observator.hasToldToObserveFirstOfPage.value = false;
    }
}
